import {
  ReactNode,
  useEffect,
  SyntheticEvent,
  useCallback,
  useContext,
} from 'react';
import {css, view, Icon, Link} from '@sail/ui/deprecated';
import {warning} from '@sail/icons/react/Icon';
import {useTheme, useWidgetTitle} from '@stripe-internal/conversations-ui';
import {safeWindowOpen} from '@stripe-internal/safe-links';
import {defineMessages, FormattedMessage} from 'react-intl';
import {ConfigContext} from '../Providers/ConfigProvider';
import {useMetrics} from '../Providers/ObservabilityProvider';
import {useMountEffect} from '../useMountEffect';
import {Title} from '../components/Title';

export const messages = defineMessages({
  title: {
    id: 'support_conversations_client.unrecoverableErrorScreen.title',
    description: 'Title shown on the screen for an unrecoverable error',
    defaultMessage: 'Something went wrong',
  },
  sessionInvalidTitle: {
    id: 'support_conversations_client.unrecoverableErrorScreen.invalidSessionTitle',
    description:
      'Title shown on the screen for an unrecoverable error when the user session has expired',
    defaultMessage: 'Your session has expired',
  },
  description: {
    id: 'support_conversations_client.unrecoverableErrorScreen.description',
    description:
      'Additional information shown to the user for an unrecoverable error',
    defaultMessage:
      '<contact-link>Contact us</contact-link> with details about your issue.',
  },
  descriptionWithSignInSuggestion: {
    id: 'support_conversations_client.unrecoverableErrorScreen.descriptionWithSignInSuggestion',
    description:
      'Additional information shown to the user for an unrecoverable error, including a suggestion to try signing in again.',
    defaultMessage:
      'Try signing in again or <contact-link>contact us</contact-link> with details about your issue.',
  },
  descriptionWithInvalidSessionMessage: {
    id: 'support_conversations_client.unrecoverableErrorScreen.descriptionWithInvalidSessionMessage',
    description:
      'Additional information shown to the user for an unrecoverable error, including a message telling the user their session expired.',
    defaultMessage: 'Refresh the page or sign in again to continue.',
  },
});

type Props = {
  onContactClick?: () => void;
  includeSignInSuggestion?: boolean;
  sentryEventId?: string;
  showInvalidSessionMessage?: boolean;
};

export function UnrecoverableErrorScreen({
  onContactClick,
  includeSignInSuggestion,
  sentryEventId,
  showInvalidSessionMessage,
}: Props) {
  const theme = useTheme();
  const {setTitle} = useWidgetTitle();
  const {urls, parentOrigin, token} = useContext(ConfigContext);
  const emailContactUrlDefault = `${urls.supportSite}/contact/email?token=${token}`;
  const metrics = useMetrics();

  useEffect(() => {
    setTitle(<Title />);
  }, [setTitle]);

  useMountEffect(() => {
    metrics.increment('support_conversations_error_screen_mount');
  });

  const onContactClickDefault = useCallback(
    (e: SyntheticEvent) => {
      // If we're viewing the widget on the correct support site, open the contact
      // form in the current tab. Otherwise open it in a new tab so the user
      // doesn't navigate off of the dashboard or whatever surface they're on.
      const target =
        parentOrigin !== '' && emailContactUrlDefault.startsWith(parentOrigin)
          ? '_top'
          : '_blank';
      safeWindowOpen(emailContactUrlDefault, target);
      e.preventDefault();
    },
    [emailContactUrlDefault, parentOrigin],
  );

  const renderContactLink = useCallback(
    (value: ReactNode[]) => (
      // We include href here even though we use a click handler so that
      // the URL will appear in the browser tooltips or status bars.
      // If we pass in a custom onClick handler + url, we'll use that instead.
      <Link
        type="primary"
        href={onContactClick ? undefined : emailContactUrlDefault}
        onClick={onContactClick || onContactClickDefault}
        style={{color: theme.linkColor}}
      >
        {value}
      </Link>
    ),
    [
      onContactClick,
      emailContactUrlDefault,
      onContactClickDefault,
      theme.linkColor,
    ],
  );

  let errorTitle = messages.title;
  let errorDescription = messages.description;

  if (showInvalidSessionMessage) {
    errorTitle = messages.sessionInvalidTitle;
    errorDescription = messages.descriptionWithInvalidSessionMessage;
  } else if (includeSignInSuggestion) {
    errorDescription = messages.descriptionWithSignInSuggestion;
  }

  return (
    <view.div
      css={{
        width: css.value('100%'),
        height: css.value('100%'),
        textAlign: css.value('center'),
        stack: 'y',
        paddingX: 'large',
      }}
      data-testid="error-screen"
      // We want to alert screen readers immediately if there's been an error
      aria-live="assertive"
    >
      <view.div
        css={{
          stack: 'y',
          height: 'fill',
          gap: 'small',
          alignX: 'center',
          alignY: 'center',
        }}
      >
        <Icon icon={warning} size="large" />
        <view.div css={{font: 'subtitle'}}>
          <FormattedMessage {...errorTitle} />
        </view.div>
        <view.div css={{font: 'body'}}>
          <FormattedMessage
            {...errorDescription}
            values={{'contact-link': renderContactLink}}
          />
        </view.div>
      </view.div>
      {sentryEventId && (
        <view.p css={{font: 'caption', color: 'secondary'}}>
          {sentryEventId}
        </view.p>
      )}
    </view.div>
  );
}
